import React from "react";
import { SocialLinks } from "../social/SocialLinks";
import { useMemorial } from "@/contexts/MemorialContext";
import { useLinks } from "@/hooks/useLinks";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { GraduationCap, Briefcase, Heart, Sparkles } from "lucide-react";

interface PersonalityDetails {
  education: string | null;
  profession: string | null;
  spirituality: string | null;
  interests: string[];
}

export const InfoCards = () => {
  const { id } = useMemorial();

  const { links } = useLinks(id);
  const [details, setDetails] = useState<PersonalityDetails>({
    education: null,
    profession: null,
    spirituality: null,
    interests: []
  });

  useEffect(() => {
    const fetchPersonalityDetails = async () => {
      if (!id) {
        return;
      }

      try {
        // Execute query
        const { data, error } = await supabase
          .from('interests_and_personality')
          .select('education, profession, spirituality, interests')
          .eq('memorial_id', id)
          .maybeSingle();

        if (error) {
          console.error("[INFO_CARDS] Error fetching personality details:", error);
          return;
        }

        if (!data) {
          return;
        }

        setDetails({
          education: data?.education || null,
          profession: data?.profession || null,
          spirituality: data?.spirituality || null,
          interests: data?.interests || []
        });

      } catch (err) {
        console.error("[INFO_CARDS] Unexpected error:", err);
      }
    };

    if (id) {
      fetchPersonalityDetails();
    }
  }, [id]);

  // Format education entries as bullet points showing institution and level together
  const formatEducation = (education: string | null) => {
    if (!education) return null;
    
    // Split by '|' to get separate education entries
    const educationEntries = education.split('|').filter(entry => entry.trim());
    
    if (educationEntries.length === 0) return null;
    
    return (
      <ul className="list-disc list-inside space-y-1">
        {educationEntries.map((entry, index) => {
          // For each entry, replace @ with " - " to format as "Institution - Level"
          const formattedEntry = entry.trim().replace('@', ' - ');
          return (
            <li key={index} className="text-muted-foreground">
              {formattedEntry}
            </li>
          );
        })}
      </ul>
    );
  };

  // Format profession in the "[Position] at [company]" format
  const formatProfession = (profession: string | null) => {
    if (!profession) return "No profession information added";
    
    // Check if the profession contains the "@" separator
    if (profession.includes('@')) {
      // Split by "@" and format as "[Position] at [company]"
      const [position, company] = profession.split('@');
      return `${position.trim()} at ${company.trim()}`;
    }
    
    // If no "@" separator, return as is
    return profession;
  };

  // Format spirituality as multiple items if the data contains the '|' separator
  const formatSpirituality = (spirituality: string | null) => {
    if (!spirituality) return "No spirituality information added";
    
    const affiliations = spirituality.split('|').filter(item => item.trim() !== '');
    
    if (affiliations.length === 0) return "No spirituality information added";
    
    if (affiliations.length === 1) return affiliations[0];
    
    return (
      <div className="flex flex-wrap gap-2">
        {affiliations.map((affiliation, index) => (
          <span key={index} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
            {affiliation}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
            <GraduationCap className="h-5 w-5 text-primary mr-2" />
            <h3 className="font-medium text-foreground">Education</h3>
          </div>
          <div className="p-6">
            {details.education ? 
              formatEducation(details.education) : 
              <p className="text-muted-foreground">No Education Details Have Been Added Yet</p>
            }
          </div>
        </div>
        
        <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
            <Briefcase className="h-5 w-5 text-primary mr-2" />
            <h3 className="font-medium text-foreground">Profession</h3>
          </div>
          <div className="p-6">
            <p className="text-muted-foreground">
              {details.profession ? formatProfession(details.profession) : "No profession information added"}
            </p>
          </div>
        </div>
        
        <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
            <Heart className="h-5 w-5 text-primary mr-2" />
            <h3 className="font-medium text-foreground">Spirituality</h3>
          </div>
          <div className="p-6">
            <div className="text-muted-foreground">
              {details.spirituality ? formatSpirituality(details.spirituality) : "No spirituality information added"}
            </div>
          </div>
        </div>
        
        <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
            <Sparkles className="h-5 w-5 text-primary mr-2" />
            <h3 className="font-medium text-foreground">Interests & Hobbies</h3>
          </div>
          <div className="p-6">
            {details.interests && details.interests.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {details.interests.map((interest, index) => (
                  <span key={index} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm">
                    {interest}
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-muted-foreground">No interests or hobbies added</p>
            )}
          </div>
        </div>
      </div>
      
      {/* Social links styled as a card with proper heading */}
      {links && links.length > 0 && (
        <div className="bg-card rounded-xl shadow-sm border overflow-hidden transition-all duration-300 hover:shadow-md">
          <div className="border-b bg-muted/30 px-6 py-4 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <h3 className="font-medium text-foreground">Connect & Remember</h3>
          </div>
          <div className="p-6">
            <SocialLinks links={links} showLabels={true} />
          </div>
        </div>
      )}
    </div>
  );
};
